import { Link } from 'react-router-dom'
import { ChevronRight, ArrowRight, ShieldAlert, UserMinus, Activity } from 'lucide-react'
import { SECONDARY_CTA_TEXT } from '../../../../config/cta'
import LoopCTAs from '../../../../components/LoopCTAs'
import Seo from '../../../../components/Seo'

const subCategories = [
  {
    icon: ShieldAlert,
    title: 'Churn Prevention',
    description: 'Detect at-risk accounts early and intervene before a 7 becomes a cancellation. Proactive conversations that surface warning signs while there is still time to act.',
    href: '/feedback-loops/customer/churn/prevention',
    color: 'text-prism-cyan',
    bgColor: 'bg-prism-cyan/10',
  },
  {
    icon: UserMinus,
    title: 'Churn Analysis',
    description: 'Understand why customers leave, in their own words. Post-churn conversations that capture the real story, not the polite excuse.',
    href: '/feedback-loops/customer/churn/analysis',
    color: 'text-prism-red',
    bgColor: 'bg-prism-red/10',
  },
  {
    icon: Activity,
    title: 'Churn Reduction',
    description: 'AI-powered churn intelligence that identifies the specific drivers of attrition, with ROI modeling, comparison benchmarks, and a roadmap from pilot to program.',
    href: '/feedback-loops/customer/churn/reduction',
    color: 'text-prism-orange',
    bgColor: 'bg-prism-orange/10',
  },
]

const stats = [
  { number: '5x', label: 'more expensive to acquire a new customer than retain one' },
  { number: '25-95%', label: 'profit increase from just 5% improvement in retention' },
  { number: '$195B', label: 'annual cost of customer churn across U.S. banking alone' },
]

const whatWeUncover = [
  'Early warning signals before customers decide to leave',
  'Fee and policy friction that creates silent frustration',
  'Digital experience gaps vs. customer expectations',
  'Competitive intelligence — what draws customers away',
  'Segment-level risk and what drives attrition in each',
  'Retention drivers — what would make at-risk customers stay',
]

export default function ChurnResearchIndex() {
  return (
    <div>
      <Seo
        title="Churn Research"
        description="Prevent, analyze, and reduce customer churn with AI-powered conversations. Understand why customers really leave — three approaches, one goal."
        path="/feedback-loops/customer/churn"
      />

      {/* Breadcrumbs */}
      <div className="bg-white border-b border-intelligence-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center text-sm text-intelligence-500">
            <Link to="/" className="hover:text-intelligence-900">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/feedback-loops" className="hover:text-intelligence-900">Use Cases</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-intelligence-900">Churn Research</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-intelligence-900 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-5">
            Churn Research
          </h1>
          <p className="text-lg text-intelligence-400 max-w-2xl mx-auto mb-8">
            Stop guessing why customers leave. PrismScope&apos;s conversational intelligence platform
            gives you the tools to prevent, analyze, and reduce churn — with real customer voices, not assumptions.
          </p>
          <a
            href="https://rays.prismscope.ai/i/int_ONF-JNX1Zq0CyFEt0jzyGA?skip=1"
            className="inline-flex items-center gap-2 px-8 py-4 text-lg font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-md transition-colors"
          >
            {SECONDARY_CTA_TEXT} <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-intelligence-50 border-b border-intelligence-200 py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {stats.map((stat) => (
              <div key={stat.number}>
                <div className="text-3xl md:text-4xl font-bold text-prism-red mb-1">{stat.number}</div>
                <p className="text-sm text-intelligence-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sub-categories */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
              Three approaches. One goal.
            </h2>
            <p className="text-lg text-intelligence-600 max-w-2xl mx-auto">
              Whether you need to catch problems early, understand why customers left, or build a full churn intelligence program — we have you covered.
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {subCategories.map((sub) => (
              <Link
                key={sub.title}
                to={sub.href}
                className="group bg-white rounded-xl border border-intelligence-200 p-8 hover:shadow-lg hover:border-primary-300 transition-all flex flex-col"
              >
                <div className={`w-14 h-14 ${sub.bgColor} rounded-xl flex items-center justify-center mb-5`}>
                  <sub.icon className={`w-7 h-7 ${sub.color}`} />
                </div>
                <h3 className="text-xl font-bold text-intelligence-900 mb-3">{sub.title}</h3>
                <p className="text-intelligence-600 mb-6 flex-1">{sub.description}</p>
                <span className="text-primary-600 font-medium group-hover:text-primary-700 inline-flex items-center">
                  Learn more <ArrowRight className="ml-2 w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* What We Uncover */}
      <section className="bg-intelligence-50 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-4">
              What PrismScope surfaces
            </h2>
            <p className="text-lg text-intelligence-600">
              The intelligence you need, in your customers&apos; own words.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {whatWeUncover.map((item) => (
              <div
                key={item}
                className="flex items-start bg-white border border-intelligence-200 rounded-lg p-5"
              >
                <span className="w-2 h-2 rounded-full bg-primary-500 mt-2 mr-4 flex-shrink-0" />
                <span className="text-intelligence-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary-600 py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Let&apos;s find out why your customers are leaving.
          </h2>
          <p className="text-lg text-primary-100 mb-8">
            Start with a focused pilot: 50 AI-powered conversations and an actionable intelligence report.
          </p>
          <LoopCTAs location="feedback-loop-churn-index-final" align="center" />
        </div>
      </section>
    </div>
  )
}
